import { colorSystem, Flexbox, Text, Tooltip } from '@luminovo/design-system';
import { styled } from '@mui/material';
import React from 'react';

import { type ChartSeriesMeta } from '../types';

type ChartLegendProps<TKeys extends string> = {
    keys: TKeys[];
    seriesMeta: Record<TKeys, ChartSeriesMeta>;
    selectedKeys: TKeys[];
    staticKeys?: TKeys[];
    onToggleKey: (key: TKeys) => void;
};

const LegendButton = styled('button')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '2px 4px',
    margin: '0 -4px',
    border: 'none',
    borderRadius: 4,
    background: 'none',
    font: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { background: colorSystem.neutral[1] },
    '&:focus-visible': { outline: `2px solid ${colorSystem.primary[5]}`, outlineOffset: '0px' },
});

/**
 * Lists a chart's series with their colors. Clicking a series calls `onToggleKey`; the caller decides what that does.
 * Static keys, and a lone toggleable series, are shown without a toggle.
 */
export function ChartLegend<TKeys extends string>({
    keys,
    seriesMeta,
    selectedKeys,
    onToggleKey,
    staticKeys = [],
}: ChartLegendProps<TKeys>) {
    const toggleable = keys.filter((key) => !staticKeys.includes(key));
    // A single series needs no legend, unless it sits beside a static one such as Baseline.
    if (keys.length <= 1 && toggleable.length === keys.length) {
        return null;
    }

    return (
        <Flexbox gap="12px" sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
            {keys.map((key) => {
                const meta = seriesMeta[key];
                if (!meta) return null;
                // Hiding the only toggleable series would leave nothing to show.
                const isStatic = toggleable.length <= 1 || staticKeys.includes(key);
                const isActive = isStatic || selectedKeys.includes(key);

                const content = (
                    <>
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: 2,
                                boxSizing: 'border-box',
                                background: isActive ? meta.color : 'transparent',
                                border: `2px solid ${meta.color}`,
                                flexShrink: 0,
                            }}
                        />
                        <Text variant="body-small" color={isActive ? colorSystem.neutral[8] : colorSystem.neutral[7]}>
                            {meta.label}
                        </Text>
                    </>
                );
                const item = isStatic ? (
                    <Flexbox key={key} gap="6px" sx={{ alignItems: 'center' }}>
                        {content}
                    </Flexbox>
                ) : (
                    <LegendButton key={key} type="button" aria-pressed={isActive} onClick={() => onToggleKey(key)}>
                        {content}
                    </LegendButton>
                );

                return meta.tooltip ? (
                    // describeChild: the tooltip explains the item; it mustn't replace the button's name.
                    <Tooltip key={key} title={meta.tooltip} describeChild>
                        {item}
                    </Tooltip>
                ) : (
                    item
                );
            })}
        </Flexbox>
    );
}
